// Bubble text content for Mission 0 with highlights, keyboard layout conditionals, and fellow emotion.
// Bubble size is automatic based on text length.
// Optional per-page bubble layout overrides:
// - bubbleLeftPercent / bubbleBottomPercent
// - bubbleMinWidthPercent / bubbleMaxWidthPercent
// Optional per-page fellow emotion:
// - fellowEmotion: 'hq-happy' | 'hq-impressed' | 'hq-neutral' | 'hq-speaking'
// Each entry: { text: string, highlights: Array<{text: string, conditional?: object}> }

const DEFAULT_LAYOUT_LABELS = {
 KeyZ: 'Z',
 Slash: '/',
 KeyS: 'S',
 KeyD: 'D',
 KeyF: 'F',
 KeyK: 'K',
 KeyL: 'L',
 Semicolon: ';',
};

export const MISSION_ZERO_BUBBLE_TEXT = [
 // 1
 {
  text: `Welcome to Gearshift Fellowship!\n\nI’m your fellow, here to help you discover your unique skills through fun missions. \n\nPlease turn on your sound.`,
  audioSrc: 'assets/audio/instructions 0.01.mp3',
  fellowEmotion: 'hq-happy',
  highlights: [
   { text: 'Gearshift Fellowship' },
   { text: 'fellow' },
   { text: 'sound' },
  ],
 },
 // 2
 {
  text: `Cars from different mafias are hiding packages.\n\nYour goal:\nCollect as many packages as you can before they disappear.  \n\nTo do this, you’ll go through a few steps.`,
  audioSrc: 'assets/audio/instructions 0.02.mp3',
  fellowEmotion: 'hq-impressed',
  highlights: [
   { text: 'Collect as many packages as you can before they disappear.' },
  ],
 },
 // 3
 {
  text: `Here are the steps to collect each package: \n1: Approach the target\n2: Identify the mafia type\n3: Crack the code\n4: Move into the correct lane\n5: Learn from feedback\n\nI’ll explain each step next.`,
  audioSrc: 'assets/audio/instructions 0.03.mp3',
  fellowEmotion: 'hq-speaking',
  highlights: [
   { text: 'Approach' },
   { text: 'Identify' },
   { text: 'Crack' },
   { text: 'Move' },
   { text: 'Learn' },
  ],
 },
 // 4
 {
  text: `First, you’ll see a blurred target in front of you.\n\nTap (don’t hold) "{{KeyZ}}" to slow down or tap "{{Slash}}" to speed up. \n\nWhen close enough, press the space bar with your thumb to lock on. The car will then appear.`,
  audioSrc: 'assets/audio/instructions 0.04.mp3',
  fellowEmotion: 'hq-neutral',
  highlights: [
   { conditional: { key: 'KeyZ' } },
   { text: 'slow down' },
   { conditional: { key: 'Slash' } },
   { text: 'speed up' },
   { text: 'space bar' },
   { text: 'thumb' },
   { text: 'lock on' },
  ],
 },
 // 5
 {
  text: `When you lock on: A label (B,N, or L) shows the mafia type (see the right box above).\n\nA code will also appear (see the gray window above at the top). \n\nNext, I’ll show you how to use the mafia-type and the code.`,
  audioSrc: 'assets/audio/instructions 0.05.mp3',
  fellowEmotion: 'hq-speaking',
  highlights: [
   { text: 'A label' },
   { text: 'A code' },
  ],
 },
 // 6
 {
  text: `Each mafia type cares about one part of the code (see the right box above). \nCars with labels:\n\n  • B (Brightness): Decide if the background (ignoring letter & number) is more dark or bright?\n  • N (Number): Decide if the number is odd (1,3,...) or even (2,4,...)?\n  • L (Letter): Decide if the letter  is a consonant (K, L,...) or vowel (A, E,...)?\n\nContinue to see the next step.`,
  audioSrc: 'assets/audio/instructions 0.06.mp3',
  fellowEmotion: 'hq-impressed',
  highlights: [
   { text: '• B (Brightness)' },
   { text: 'background (ignoring letter & number)' },
   { text: '• N (Number)' },
   { text: 'number' },
   { text: '• L (Letter)' },
   { text: 'letter' },
  ],
 },
 // 7
 {
  text: `Each answer has its own lane and key as shown above.\nMove into the correct lane to collect the package. \nFamiliarize yourself now with the mapping above.\n\nDon’t worry - you’ll get practice before the game starts.\nDuring the game, headers above the screen will remind you of the finger placement.`,
  audioSrc: 'assets/audio/instructions 0.07.mp3',
  fellowEmotion: 'hq-happy',
  highlights: [
   { text: 'Each answer has its own lane and key' },
  ],
 },
 // 8
 {
  text: `Some quick reminders:\n\nThe bottom of the screen will always show the key mapping \n(see the icons in the blue frame above). \n\nAdditional Icons remind you of the mafia types (see the right box). \n\nThe speedometer on the bottom right will show your current speed.`,
  audioSrc: 'assets/audio/instructions 0.08.mp3',
  fellowEmotion: 'hq-neutral',
  highlights: [
   { text: 'bottom' },
   { text: 'key mapping' },
  ],
 },
 // 9
 {
  text: `Once you move to a lane, the package appears in the correct lane. \n\nSo, the package’s lane tells you the correct answer and the car’s mafia type. \n\nAn icon above the car will remind you of the correct mafia type (B=Brightness, N=Number, L=Letter). `,
  audioSrc: 'assets/audio/instructions 0.09.mp3',
  fellowEmotion: 'hq-happy',
  highlights: [
   { text: 'package' },
   { text: 'correct lane' },
  ],
 },
 // 10
 {
  text: `You’ll also get feedback from the motorcycle driver:\n\n  • The driver is happy if you did everything correctly.\n  • The driver is sad if you chose the wrong mafia.\n  • The driver is surprised if you solved the code incorrectly (wrong category) but you chose the correct mafia type.\n\n You’ll also hear different sounds when the driver is happy, sad, or surprised.`,
  audioSrc: 'assets/audio/instructions 0.10.mp3',
  fellowEmotion: 'hq-impressed',
  highlights: [
   { text: 'happy' },
   { text: 'correctly' },
   { text: 'sad' },
   { text: 'wrong mafia' },
   { text: 'surprised' },
   { text: 'code incorrectly (wrong category)' },
   { text: 'correct mafia type' },
  ],
 },
 // 11
 {
  text: `You’ll be driving from a motorcyclist’s point of view. Look at the picture above to familiarize yourself with that view.\n\nI’ll be with you and show important messages in the window above.`,
  audioSrc: 'assets/audio/instructions 0.11.mp3',
  fellowEmotion: 'hq-speaking',
  highlights: [
   { text: 'important messages' },
  ],
 },
 // 12
 {
  text: `You start with 5 lives. If you lose them, points will buy new ones automatically.`,
  audioSrc: 'assets/audio/instructions 0.12.mp3',
  fellowEmotion: 'hq-neutral',
  highlights: [
   { text: '5 lives' },
  ],
 },
 // 13
 {
  text: `Sometimes a bomb appears above. Make your choice before it explodes.`,
  audioSrc: 'assets/audio/instructions 0.13.mp3',
  fellowEmotion: 'hq-impressed',
  highlights: [
   { text: 'bomb' },
  ],
 },
 // 14
 {
  text: `From time to time, I'll check in with you.\n\nA set of emoticons will appear.\nChoose the one that best matches how you feel in that moment.\nThe corresponding key will be shown under each icon.`,
  audioSrc: 'assets/audio/instructions 2.5.mp3',
  fellowEmotion: 'hq-happy',
  highlights: [
   { text: 'emoticons' },
   { text: 'how you feel' },
   { text: 'key' },
   { text: 'under each icon' },
  ],
 },
 // 15
 {
  text: `Some keyboard tips: \n\n  • Rest your fingers on the keys as shown above.\n  • To make a response, press keys down without lifting your fingers. Tap, don’t hold keys down.\n  • You can adjust speed when approaching cars AND packages in your lane.\n  • To slow down: Tap (don’t hold) the {{KeyZ}} key.\n  • To speed up: Tap (don’t hold) the {{Slash}} key.`,
  audioSrc: 'assets/audio/instructions 0.14.mp3',
  fellowEmotion: 'hq-speaking',
  highlights: [
   { text: 'fingers' },
   { text: 'without lifting your fingers' },
   { text: 'adjust speed' },
   { text: 'approaching cars AND packages' },
   { text: 'slow down' },
   { text: 'speed up' },
  ],
 },
 // 16
 {
  text: `You can go back to review if you like. Once you continue, the game will start right away. \n\nFinal reminders:\n\n  • Tap keys, don’t hold.\n  • The driver automatically goes back to the center following a crash.\n  • Mistakes are normal - just keep going!`,
  audioSrc: 'assets/audio/instructions 0.15.mp3',
  fellowEmotion: 'hq-happy',
  highlights: [
   { text: 'the game will start right away' },
  ],
 },
];

function buildLabelsFromLayoutConfig(layoutConfig) {
 return {
  ...DEFAULT_LAYOUT_LABELS,
  KeyZ: layoutConfig?.backwardSlowDownKey ?? DEFAULT_LAYOUT_LABELS.KeyZ,
  Slash: layoutConfig?.forwardSpeedUpKey ?? DEFAULT_LAYOUT_LABELS.Slash,
  KeyS: layoutConfig?.KeyS ?? DEFAULT_LAYOUT_LABELS.KeyS,
  KeyD: layoutConfig?.KeyD ?? DEFAULT_LAYOUT_LABELS.KeyD,
  KeyF: layoutConfig?.KeyF ?? DEFAULT_LAYOUT_LABELS.KeyF,
  KeyK: layoutConfig?.KeyK ?? DEFAULT_LAYOUT_LABELS.KeyK,
  KeyL: layoutConfig?.KeyL ?? DEFAULT_LAYOUT_LABELS.KeyL,
  Semicolon: layoutConfig?.Semicolon ?? DEFAULT_LAYOUT_LABELS.Semicolon,
 };
}

export function getKeyLabel(key, layoutConfig) {
 const labels = buildLabelsFromLayoutConfig(layoutConfig);
 return labels[key] || key;
}
